import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, Loader2, ShieldCheck, ArrowRight, AlertCircle } from "lucide-react";
import { useLang } from "../../i18n";
import { Reveal, Eyebrow } from "./shared";
import { submitEarlyAccess } from "../../lib/api";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

const emailRe = /^[A-Za-z0-9._%+\-]+@[A-Za-z0-9.\-]+\.[A-Za-z]{2,}$/;

// Static animation presets (defined once, not re-created per render).
const ANIM_FADE_UP = { initial: { opacity: 0, y: 8 }, animate: { opacity: 1, y: 0 } };
const ANIM_FADE = { initial: { opacity: 0 }, animate: { opacity: 1 } };

export const EarlyAccess = ({ onSuccess }) => {
  const { t, lang } = useLang();
  const [form, setForm] = useState({ name: "", email: "", company: "", companyType: "", bidFrequency: "", bottleneck: "" });
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const companyTypeKeys = ["sme", "consulting", "it_services", "engineering", "energy_construction", "agency", "public_contractor", "other"];
  const freqKeys = ["monthly", "quarterly", "rarely", "new"];
  const bottleneckKeys = ["tenders", "partners", "coordination", "documents"];

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim() || !form.email.trim() || !form.company.trim() || !form.companyType) { setError(t.form.errRequired); return; }
    if (!emailRe.test(form.email.trim())) { setError(t.form.errEmail); return; }
    setStatus("loading");
    try {
      await submitEarlyAccess({ ...form, email: form.email.trim().toLowerCase(), language: lang });
      setStatus("success");
      onSuccess && onSuccess();
    } catch (err) {
      setStatus("idle");
      setError(err?.response?.status === 409 ? t.form.errDuplicate : t.form.errGeneric);
    }
  };

  const reset = () => { setForm({ name: "", email: "", company: "", companyType: "", bidFrequency: "", bottleneck: "" }); setStatus("idle"); setError(""); };

  const Field = ({ label, children }) => (
    <div><Label className="text-[13px] font-medium text-slate-600">{label}</Label><div className="mt-1.5">{children}</div></div>
  );

  return (
    <section id="early-access" className="border-t border-[var(--border)] bg-white">
      <div className="mx-auto max-w-[1180px] px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-5">
            <Eyebrow>{t.form.eyebrow}</Eyebrow>
            <h2 className="font-heading mt-4 text-[1.65rem] font-extrabold leading-[1.14] tracking-tight text-[var(--ink)] [text-wrap:balance] sm:text-[2.1rem] lg:text-[2.6rem]">{t.form.title}</h2>
            <p className="mt-4 max-w-sm text-lg text-[var(--slate)]">{t.form.subline}</p>
            <p className="mt-8 flex items-center gap-2 text-sm text-slate-400"><ShieldCheck className="h-4 w-4 text-[var(--blue)]" strokeWidth={1.7} />{t.form.reassurance}</p>
          </div>
          <Reveal className="lg:col-span-7">
            <div className="rounded-2xl border border-[var(--border)] bg-white p-6 shadow-lg-soft sm:p-8">
              <AnimatePresence mode="wait">
                {status === "success" ? (
                  <motion.div key="ok" {...ANIM_FADE_UP} className="flex flex-col items-center py-10 text-center" data-testid="early-access-success-message">
                    <span className="grid h-14 w-14 place-items-center rounded-full bg-[var(--blue-soft)] text-[var(--blue)]"><CheckCircle2 className="h-7 w-7" strokeWidth={1.7} /></span>
                    <h3 className="font-heading mt-4 text-2xl font-bold text-[var(--ink)]">{t.form.successTitle}</h3>
                    <p className="mt-2 max-w-sm text-sm text-[var(--slate)]">{t.form.successBody}</p>
                    <button onClick={reset} data-testid="early-access-again-button" className="mt-6 rounded-full border border-[var(--border)] bg-white px-4 py-2 text-sm font-medium text-slate-600 hover:bg-[var(--surface-2)]">{t.form.again}</button>
                  </motion.div>
                ) : (
                  <motion.form key="form" {...ANIM_FADE} onSubmit={submit} noValidate data-testid="early-access-form" className="space-y-4">
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <Field label={`${t.form.name} *`}><Input data-testid="early-access-name-input" placeholder={t.form.namePh} value={form.name} onChange={(e) => set("name")(e.target.value)} /></Field>
                      <Field label={`${t.form.email} *`}><Input type="email" data-testid="early-access-email-input" placeholder={t.form.emailPh} value={form.email} onChange={(e) => set("email")(e.target.value)} /></Field>
                      <Field label={`${t.form.company} *`}><Input data-testid="early-access-company-input" placeholder={t.form.companyPh} value={form.company} onChange={(e) => set("company")(e.target.value)} /></Field>
                      <Field label={`${t.form.companyType} *`}>
                        <Select value={form.companyType} onValueChange={set("companyType")}>
                          <SelectTrigger data-testid="early-access-company-type-select"><SelectValue placeholder={t.form.companyTypePh} /></SelectTrigger>
                          <SelectContent>{companyTypeKeys.map((k) => (<SelectItem key={k} value={k} data-testid={`company-type-${k}`}>{t.companyTypes[k]}</SelectItem>))}</SelectContent>
                        </Select>
                      </Field>
                    </div>
                    <div className="grid grid-cols-1 gap-4 rounded-2xl border border-[var(--border)] bg-[var(--surface-2)]/60 p-4 sm:grid-cols-2">
                      <Field label={t.form.q1}>
                        <Select value={form.bidFrequency} onValueChange={set("bidFrequency")}>
                          <SelectTrigger data-testid="early-access-survey-frequency-select" className="bg-white"><SelectValue placeholder={t.form.q1Ph} /></SelectTrigger>
                          <SelectContent>{freqKeys.map((k) => (<SelectItem key={k} value={k} data-testid={`frequency-${k}`}>{t.frequencies[k]}</SelectItem>))}</SelectContent>
                        </Select>
                      </Field>
                      <Field label={t.form.q2}>
                        <Select value={form.bottleneck} onValueChange={set("bottleneck")}>
                          <SelectTrigger data-testid="early-access-survey-bottleneck-select" className="bg-white"><SelectValue placeholder={t.form.q2Ph} /></SelectTrigger>
                          <SelectContent>{bottleneckKeys.map((k) => (<SelectItem key={k} value={k} data-testid={`bottleneck-${k}`}>{t.bottlenecks[k]}</SelectItem>))}</SelectContent>
                        </Select>
                      </Field>
                    </div>
                    {error && (<div className="flex items-center gap-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" role="alert" aria-live="polite" data-testid="early-access-error-message"><AlertCircle className="h-4 w-4 shrink-0" strokeWidth={1.8} />{error}</div>)}
                    <button type="submit" disabled={status === "loading"} data-testid="early-access-submit-button" className="group flex w-full items-center justify-center gap-2 rounded-full bg-[var(--blue)] px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-[var(--blue-2)] disabled:opacity-70">
                      {status === "loading" ? (<><Loader2 className="h-4 w-4 animate-spin" />{t.form.submitting}</>) : (<>{t.form.submit}<ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" strokeWidth={2} /></>)}
                    </button>
                  </motion.form>
                )}
              </AnimatePresence>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
